import { ui } from '@procuros/datachecks';

import { getCanonical } from 'services/datachecks/datachecks';
import { getPackingListEnabled } from 'stores/packingList/packingList';
import { stripDefaultTransportUnitFromValues } from 'support/helpers/packingList/defaultTransportUnit';
import { enrichDocument } from 'support/helpers/transformer/transformer';
import { DocumentType, ProcessSpecificationDTO } from 'support/types';

/**
 * Flattened React Hook Form values → enriched canonical payload. This is the single submit-side
 * seam shared by create, edit, and reprocess: it strips the default placeholder transport unit and
 * its unplaced items before canonicalizing, so we send exactly the real packing structure.
 *
 * The strip defaults to the current webedi store capability. Callers that precompute work across
 * renders can pass the capability explicitly so that cached work is invalidated when it changes.
 * Stripping stays out of `getCanonical`, which is also used for live UI re-derivation where the
 * placeholder must remain visible. See docs/decisions/packing-lists.md.
 *
 * With the packing list on, a dispatch response asks the enricher to derive each transport unit's
 * `contained_trade_unit_count` (the SSCC label quantity). The shipping notice gets its count from
 * the dispatch response in the backend, so it does not ask.
 */
export const flattenedDataToEnrichedCanonical = async (
  processSpecificationFields: ProcessSpecificationDTO['fields'],
  documentType: DocumentType,
  flattenedData: unknown,
  packingListEnabled = getPackingListEnabled(),
) => {
  const valuesToSubmit =
    flattenedData && typeof flattenedData === 'object'
      ? stripDefaultTransportUnitFromValues(flattenedData as Record<string, unknown>, packingListEnabled)
      : flattenedData;
  const { canonical } = await getCanonical(processSpecificationFields, valuesToSubmit);
  const deriveContainedTradeUnitCount = packingListEnabled && documentType === DocumentType.dispatchResponse;
  return enrichDocument({
    messageType: documentType,
    data: ui.stripHelperFields(canonical),
    meta: deriveContainedTradeUnitCount ? { deriveContainedTradeUnitCount } : undefined,
  }).data;
};
